import { ContainerState } from '@sas-dvr/internal-va-react-core/store/birdElement/containerState';
import { merge, of, Observable } from 'rxjs';
import { VisualElementControllerLoader } from './LoaderFactory';
import { loadVisualContainerElementController } from './loadVisualContainerElement';
import { distinctUntilChanged, map, mergeMap } from 'rxjs/operators';
import { AnyAction } from 'redux';
import { fromPortableEvent } from '../util';
import { BusyState } from '@sas-dvr/internal-va-react-core/store/busy/BusyState';
import { WarningState } from '@sas-dvr/internal-va-react-core/store/warning/WarningState';
import { DimensionUnit, WarningEvent } from '@sas-dvr/ltjs-commons';
import { selectBusyState } from '@sas-dvr/internal-va-react-core/store/selectors';
import { getMaximizeObservables } from '../util/maximizeUtils';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { PositionedModelController } from '@sas-dvr/ltjs-bird';

export const loadCompositeContainerElementController: VisualElementControllerLoader = (
  controller,
  elementController,
  unload$,
  myActions$,
  state$,
  deps
) => {
  const key = deps.getKey(elementController);
  const observables: Observable<AnyAction>[] = [];
  const primaryElementName =
    elementController?.getPrimaryVisualElementController()?.getVisualElementName() ?? '';

  // Handle busy status
  const handleBusyChange$ = state$.pipe(
    map((state) => selectBusyState(state, primaryElementName)?.isBusy),
    distinctUntilChanged(),
    mergeMap((isBusy) => of(BusyState.actions.setBusy(key, { isBusy: isBusy || false })))
  );

  // Handle warnings
  if (controller) {
    observables.push(
      fromPortableEvent<WarningEvent>(controller, WarningEvent.GENERIC).pipe(
        mergeMap(() => {
          const primaryElementWarnings = elementController
            .getPrimaryVisualElementController()
            ?.getCurrentWarningEvent()
            ?.getWarnings();
          const primaryElementWarningMessage = primaryElementWarnings?.[0]?.getMessage() || '';
          const primaryElementWarningSeverity = primaryElementWarnings?.[0]?.getSeverity();

          if (primaryElementWarningSeverity && primaryElementWarningSeverity >= 90) {
            return of(
              WarningState.actions.set(key, {
                type: 'error',
                message: primaryElementWarningMessage,
              })
            );
          } else {
            return of(WarningState.actions.set(key, undefined));
          }
        })
      )
    );
  }

  // VASDK-2352: composite container titles need extra padding to align with sub-visuals
  {
    const primaryVisualViewController = elementController
      .getReportController()
      ?.getViewControllerForName(
        elementController.getPrimaryVisualElementController()?.getVisualElementName() ?? null
      );
    const innerPadding =
      primaryVisualViewController instanceof PositionedModelController
        ? primaryVisualViewController
            .getPadding()
            ?.getScalarValueForUnit(DimensionUnit.PIXELS, -1.0, 1.0)
        : undefined;
    observables.push(
      of(
        BirdLayoutState.actions.update(key, {
          titlePadding: innerPadding,
        })
      )
    );
  }

  observables.push(getMaximizeObservables(key, controller, elementController, state$, unload$));

  const loadVisualContainerElementController$ = loadVisualContainerElementController(
    controller,
    elementController,
    unload$,
    myActions$,
    state$,
    deps,
    {
      create: () => ContainerState.actions.add(key, { isComposite: true }),
      delete: () => ContainerState.actions.delete(key),
    }
  );
  return merge(loadVisualContainerElementController$, ...observables, handleBusyChange$);
};
